// Master Admin: Blog Management + Payment Gateway settings + Admin Roles
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, FileText, CreditCard, Shield, Eye, EyeOff, ExternalLink } from "lucide-react";
import { API, getToken } from "../../lib/auth";
import { useConfirm } from "../../components/ConfirmDialog";

const cfg = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const cls = {
  card: "bg-white rounded-2xl border border-black/[0.06]",
  input: "w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm bg-white",
  label: "block text-[11px] font-bold uppercase tracking-wider text-black/55 mb-1.5",
  primary: "inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white text-sm font-bold hover:opacity-95 shadow-sm",
};

// ────────────────────────── BLOG ──────────────────────────
export function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const confirm = useConfirm();
  const load = () => axios.get(`${API}/admin/blog`, cfg()).then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);
  const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^\w]+/g, "-").replace(/^-|-$/g, "");
  const openCreate = () => { setForm({ title: "", slug: "", excerpt: "", content: "", tags: [], author: "Brivo Team", published: false, cover_url: "" }); setEdit(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p, tags: p.tags || [] }); setEdit(p); setOpen(true); };
  const submit = async () => {
    if (!form.title || !form.content) return toast.error("Title and content are required");
    if (!(await confirm({ title: edit ? "Save post?" : "Create post?", message: form.published ? "This will be live on the marketing site." : "Will be saved as draft." }))) return;
    try {
      const slug = form.slug || slugify(form.title);
      const payload = { ...form, slug };
      if (edit) await axios.patch(`${API}/admin/blog/${edit.id}`, payload, cfg());
      else await axios.post(`${API}/admin/blog`, payload, cfg());
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (p) => {
    if (!(await confirm({ title: "Delete post?", message: `"${p.title}" will be removed.`, tone: "danger", yesLabel: "Delete" }))) return;
    await axios.delete(`${API}/admin/blog/${p.id}`, cfg());
    toast.success("Deleted"); load();
  };
  return (
    <div data-testid="admin-blog-page" className="p-6 lg:p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Blog Management</h1>
          <p className="text-xs text-black/55 mt-1">Master Admin / Blog</p>
        </div>
        <button onClick={openCreate} data-testid="admin-blog-new" className={cls.primary}><Plus size={14} /> New Post</button>
      </div>
      <div className={`${cls.card} p-3`}>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr><th className="text-left p-3.5">Title</th><th className="text-left p-3.5">Slug</th><th className="text-left p-3.5">Author</th><th className="text-left p-3.5">Status</th><th className="text-left p-3.5">Published</th><th className="text-right p-3.5">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {posts.map((p) => (
              <tr key={p.id}>
                <td className="p-3.5 font-semibold">{p.title}</td>
                <td className="p-3.5 font-mono text-black/55">{p.slug}</td>
                <td className="p-3.5">{p.author}</td>
                <td className="p-3.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-black/5 text-black/55"}`}>{p.published ? "Live" : "Draft"}</span></td>
                <td className="p-3.5 text-black/55 text-xs">{(p.published_at || "").slice(0, 10) || "—"}</td>
                <td className="p-3.5 text-right">
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-black/5 inline-block mr-1"><ExternalLink size={13} /></a>
                  <button onClick={() => openEdit(p)} className="p-1.5 rounded hover:bg-black/5 mr-1"><Pencil size={13} /></button>
                  <button onClick={() => del(p)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {!posts.length && <tr><td colSpan={6} className="p-10 text-center text-black/45">No blog posts yet</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl shadow-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <div className="flex items-center gap-2"><FileText size={16} className="text-[#1FA868]" /><h3 className="font-bold">{edit ? "Edit Post" : "New Post"}</h3></div>
              <button onClick={() => setOpen(false)}><X size={15} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3 overflow-y-auto">
              <div className="col-span-2"><label className={cls.label}>Title</label><input className={cls.input} value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })} data-testid="ab-title" /></div>
              <div><label className={cls.label}>Slug</label><input className={cls.input} value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="ab-slug" /></div>
              <div><label className={cls.label}>Author</label><input className={cls.input} value={form.author || ""} onChange={(e) => setForm({ ...form, author: e.target.value })} /></div>
              <div className="col-span-2"><label className={cls.label}>Cover Image URL</label><input className={cls.input} value={form.cover_url || ""} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} placeholder="https://..." /></div>
              <div className="col-span-2"><label className={cls.label}>Excerpt</label><textarea className={`${cls.input} min-h-[60px]`} value={form.excerpt || ""} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
              <div className="col-span-2"><label className={cls.label}>Content (Markdown supported)</label><textarea className={`${cls.input} min-h-[220px] font-mono text-xs`} value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="ab-content" /></div>
              <div className="col-span-2"><label className={cls.label}>Tags (comma separated)</label><input className={cls.input} value={(form.tags || []).join(", ")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></div>
              <div className="col-span-2"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="ab-publish" /> <span className="font-semibold">Publish (live on marketing site)</span></label></div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={submit} data-testid="ab-save" className={cls.primary}><Save size={13} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── PAYMENT GATEWAY SETTINGS ──────────────────────────
export function AdminPaymentGateway() {
  const [s, setS] = useState(null);
  const [show, setShow] = useState(false);
  const [secret, setSecret] = useState("");
  const confirm = useConfirm();
  const load = () => axios.get(`${API}/admin/gateway-settings`, cfg()).then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  if (!s) return <div className="p-8">Loading…</div>;
  const save = async () => {
    if (!(await confirm({ title: "Save gateway settings?", message: "Tenants will use these credentials for payments immediately." }))) return;
    try {
      const payload = { ...s };
      if (secret) payload.paystack_secret_key = secret;
      delete payload.paystack_secret_key_masked;
      delete payload.id;
      await axios.patch(`${API}/admin/gateway-settings`, payload, cfg());
      toast.success("Gateway settings saved"); setSecret(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="admin-gateway-page" className="p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="font-display font-extrabold text-3xl tracking-tight">Payment Gateway</h1>
        <p className="text-xs text-black/55 mt-1">Master Admin / Payment Gateway</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className={`${cls.card} p-6`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-[#E8F5E9] text-[#1FA868] flex items-center justify-center"><CreditCard size={18} /></div>
            <div><h3 className="font-extrabold">Paystack</h3><p className="text-xs text-black/55">Card payments via Paystack inline</p></div>
            <label className="ml-auto inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.paystack_enabled} onChange={(e) => setS({ ...s, paystack_enabled: e.target.checked })} data-testid="pg-paystack-enabled" /> <span className="font-semibold">Enabled</span></label>
          </div>
          <div className="space-y-3">
            <div><label className={cls.label}>Public Key (sk_test_… / pk_test_…)</label><input className={cls.input} value={s.paystack_public_key || ""} onChange={(e) => setS({ ...s, paystack_public_key: e.target.value })} placeholder="pk_test_..." data-testid="pg-pk" /></div>
            <div>
              <label className={cls.label}>Secret Key {s.paystack_secret_key_masked && <span className="text-black/45 font-normal">(current: {s.paystack_secret_key_masked})</span>}</label>
              <div className="relative">
                <input type={show ? "text" : "password"} className={`${cls.input} pr-10`} value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={s.paystack_secret_key_masked ? "Leave blank to keep current" : "sk_test_..."} data-testid="pg-sk" />
                <button onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/45">{show ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.paystack_test_mode} onChange={(e) => setS({ ...s, paystack_test_mode: e.target.checked })} /> <span className="font-semibold">Test mode</span></label>
          </div>
        </div>

        <div className={`${cls.card} p-6`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><FileText size={18} /></div>
            <div><h3 className="font-extrabold">Bank Transfer</h3><p className="text-xs text-black/55">Manual transfer with proof upload</p></div>
            <label className="ml-auto inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.bank_transfer_enabled} onChange={(e) => setS({ ...s, bank_transfer_enabled: e.target.checked })} data-testid="pg-bt-enabled" /> <span className="font-semibold">Enabled</span></label>
          </div>
          <div className="space-y-3">
            <div><label className={cls.label}>Bank Name</label><input className={cls.input} value={s.bank_name || ""} onChange={(e) => setS({ ...s, bank_name: e.target.value })} data-testid="pg-bank-name" /></div>
            <div><label className={cls.label}>Account Holder</label><input className={cls.input} value={s.bank_account_holder || ""} onChange={(e) => setS({ ...s, bank_account_holder: e.target.value })} data-testid="pg-bank-holder" /></div>
            <div><label className={cls.label}>Account Number</label><input className={cls.input} value={s.bank_account_number || ""} onChange={(e) => setS({ ...s, bank_account_number: e.target.value })} data-testid="pg-bank-number" /></div>
            <div><label className={cls.label}>SWIFT / Routing (optional)</label><input className={cls.input} value={s.bank_swift || ""} onChange={(e) => setS({ ...s, bank_swift: e.target.value })} /></div>
            <div><label className={cls.label}>Instructions to customer</label><textarea className={`${cls.input} min-h-[70px]`} value={s.bank_instructions || ""} onChange={(e) => setS({ ...s, bank_instructions: e.target.value })} /></div>
          </div>
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <button onClick={save} data-testid="pg-save" className={cls.primary}><Save size={14} /> Save Gateway Settings</button>
      </div>
    </div>
  );
}
